
import React, { ReactNode, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

interface SafeNavigationProps {
  children: ReactNode;
  fallbackRoute?: string;
  className?: string;
}

/**
 * A component that provides safe navigation capabilities
 * to prevent errors related to React context during navigation
 */
export const SafeNavigation = ({ 
  children, 
  fallbackRoute = '/dashboard', 
  className 
}: SafeNavigationProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isNavigatingRef = useRef(false);
  
  // Safely navigate to a route with fallback and error handling
  const safeNavigate = (to: string) => {
    if (isNavigatingRef.current) {
      return; // Prevent multiple navigation attempts
    }
    
    try {
      isNavigatingRef.current = true;
      navigate(to);
    } catch (error) {
      console.error("Navigation error:", error);
      toast({
        title: "Erro de navegação",
        description: "Houve um problema ao navegar. Redirecionando para uma página segura.",
        variant: "destructive",
      });
      
      // Use replace to avoid adding to history stack if there's an error
      try {
        navigate(fallbackRoute, { replace: true });
      } catch (fallbackError) {
        console.error("Fallback navigation error:", fallbackError);
        // Last resort - reload the page to get to a stable state
        window.location.href = fallbackRoute;
      }
    } finally {
      // Reset the flag after a short delay to allow navigation to complete
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 500);
    }
  };
  
  return (
    <div className={className}>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, {
            onClick: (e: React.MouseEvent) => {
              // Preserve original onClick if it exists
              if (child.props.onClick) {
                child.props.onClick(e);
              }
              
              // If onClick didn't navigate (e.g. using a Link component),
              // and the child has a to prop, use our safe navigate
              if (child.props.to) {
                e.preventDefault();
                safeNavigate(child.props.to);
              }
            }
          });
        }
        return child;
      })}
    </div>
  );
};

// Helper hook to get safe navigation functions
export const useSafeNavigation = (fallbackRoute: string = '/dashboard') => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isNavigatingRef = useRef(false);
  
  const safeNavigate = (to: string) => {
    if (isNavigatingRef.current) {
      return;
    }
    
    try {
      isNavigatingRef.current = true;
      navigate(to);
    } catch (error) {
      console.error("Navigation error:", error);
      toast({
        title: "Erro de navegação",
        description: "Houve um problema ao navegar. Redirecionando para uma página segura.",
        variant: "destructive",
      });
      
      try {
        navigate(fallbackRoute, { replace: true });
      } catch (fallbackError) {
        console.error("Fallback navigation error:", fallbackError);
        window.location.href = fallbackRoute;
      }
    } finally {
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 500);
    }
  };
  
  const safeGoBack = () => {
    try {
      // Try to go back in history
      window.history.length > 1 
        ? navigate(-1) 
        : safeNavigate(fallbackRoute);
    } catch (error) {
      console.error("Navigation error:", error);
      safeNavigate(fallbackRoute);
    }
  };
  
  return { safeNavigate, safeGoBack };
};
